import React, { useEffect } from "react";
import { Button, Grid, Chip } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { BaseDrawer } from "components/Shared/Base/BaseDrawer";
import LoadingWrapper from "components/Shared/LoadingWrapper";
import InnerShadowBox from "components/Shared/InnerShadowBox";
import { BASE_CONSTANTS } from "constants/baseConstants";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseTextField from "components/Shared/Base/BaseTextField";
import { useVendorProfileDetailContext } from "contexts/VendorProfileDetailContext";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";

const INITIAL_STATE = {
    branches: [],
    vendor: "",
};

const VendorServiceBranchesForm = ({
    isBusy = false,
    vendorServiceBranchesDetail = null,
    onSubmit = () => { },
    onCancel = () => { },
    vendorId,
    vendor,
}) => {
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({ defaultValues: INITIAL_STATE });

    const [fetchBranches, { data: branches = [], isLoading: loadingBranches, error: branchError }] =
        useLazyGetDDLBranchListQuery();

    const { vendorProfileDetail } = useVendorProfileDetailContext();

  

    useEffect(() => {
        console.log("vendorServiceBranchesDetail:", vendorServiceBranchesDetail);

        if (vendorServiceBranchesDetail) {
            // Handle existing service branches data
            let existingBranches = [];

            // Check different possible data structures
            if (vendorServiceBranchesDetail?.serviceBranches && Array.isArray(vendorServiceBranchesDetail.serviceBranches)) {
                // Backend returns { serviceBranches: [branch1, branch2, ...] }
                existingBranches = vendorServiceBranchesDetail.serviceBranches.map((branch) => ({
                    label: `${branch?.branchName || branch?.name || "N/A"} - ${branch?.code || "N/A"}`,
                    id: branch._id
                }));
            } else if (Array.isArray(vendorServiceBranchesDetail)) {
                // Backend returns [branch1, branch2, ...] directly
                existingBranches = vendorServiceBranchesDetail.map((branch) => ({
                    label: `${branch?.branchName || branch?.name || "N/A"} - ${branch?.code || "N/A"}`,
                    id: branch._id
                }));
            } else if (vendorServiceBranchesDetail?.data && Array.isArray(vendorServiceBranchesDetail.data)) {
                // From list API: { data: [branch1, branch2, ...], totalItems: x }
                existingBranches = vendorServiceBranchesDetail.data.map((branch) => ({
                    label: `${branch?.branchName || branch?.name || "N/A"} - ${branch?.code || "N/A"}`,
                    id: branch._id
                }));
            }

            // Remove duplicates based on ID
            const uniqueBranches = existingBranches.filter((branch, index, self) =>
                index === self.findIndex(b => b.id === branch.id)
            );

            reset({
                branches: uniqueBranches,
                vendor: {
                    id: vendorProfileDetail?.vendor?._id || "",
                    label: vendorProfileDetail?.vendor?.name || "",
                },
            });
        } else {
            reset({
                ...INITIAL_STATE,
                vendor: {
                    id: vendorProfileDetail?.vendor?._id || "",
                    label: vendorProfileDetail?.vendor?.name || "",
                },
            });
        }
    }, [vendorServiceBranchesDetail, reset, vendorProfileDetail]);

    useEffect(() => {
        
            fetchBranches({
                account: vendorProfileDetail.account._id,
                entity: vendorProfileDetail.entity._id,
            });
        
    }, [vendorProfileDetail, fetchBranches]);

    const handleFormSubmit = (data) => {
        const payload = {
            vendorId,
            branches: data.branches?.map((branch) => branch.id) || [],
        };
        onSubmit(payload);
    };

    const mappedBranches = branches.map((branch) => ({
        label: `${branch.branchName || branch.name || "N/A"} - ${branch.code || "N/A"}`,
        id: branch._id,
    }));

    const handleCancel = () => {
        reset(INITIAL_STATE);
        onCancel();
    };

    return (
        <>
            <LoadingWrapper loading={isBusy}>
                <InnerShadowBox>
                    <BaseDrawer.Content>
                        <Grid container spacing={BASE_CONSTANTS.SPACING_LG}>
                            <Grid item md={12}>
                                <Controller
                                    name="vendor"
                                    control={control}
                                    rules={{ required: "Please select a Vendor." }}
                                    render={({ field }) => (
                                        <BaseTextField
                                            {...field}
                                            fullWidth
                                            required
                                            disabled
                                            label="Vendor"
                                            value={vendor || vendorProfileDetail?.vendor?.name || ""}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Controller
                                    name="branches"
                                    control={control}
                                    rules={{ required: "Please select at least one branch." }}
                                    render={({ field: { value, onChange } }) => (
                                        <BaseAutocomplete
                                            multiple
                                            fullWidth
                                            label="Branches"
                                            required
                                            value={value || []}
                                            onChange={(_, newValue) => {
                                                console.log("Branch selection changed:", newValue);
                                                // Remove duplicates based on ID
                                                const uniqueValues = newValue ? newValue.filter((item, index, self) =>
                                                    index === self.findIndex(v => v.id === item.id)
                                                ) : [];
                                                console.log("Unique values after change:", uniqueValues);
                                                onChange(uniqueValues);
                                            }}
                                            getOptionLabel={(option) => option?.label || ""}
                                            isOptionEqualToValue={(option, value) => {
                                                return option?.id === value?.id;
                                            }}
                                            options={mappedBranches || []}
                                            loading={loadingBranches}
                                            loadingText="Loading branches..."
                                            helperText={errors?.branches?.message || (branchError ? "Failed to load branches" : "")}
                                            error={!!errors?.branches || !!branchError}
                                            renderTags={(value, getTagProps) =>
                                                value.map((option, index) => (
                                                    <Chip
                                                        key={option.id}
                                                        variant="outlined"
                                                        label={option.label}
                                                        {...getTagProps({ index })}
                                                    />
                                                ))
                                            }
                                        />
                                    )}
                                />
                            </Grid>

                  
                        </Grid>
                    </BaseDrawer.Content>
                </InnerShadowBox>
                <BaseDrawer.Actions>
                    <Button
                        disabled={isBusy}
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={isBusy}
                        onClick={handleSubmit(handleFormSubmit)}
                    >
                        {vendorServiceBranchesDetail ? "Update" : "Add"}
                    </Button>
                </BaseDrawer.Actions>
            </LoadingWrapper>
        </>
    );
};

export default VendorServiceBranchesForm;